import { Component, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import dayjs from 'dayjs';
import { Jobopslag } from '@features/letpensiondk/letpensiondk-jobopslag/jobopslag.model';
import { JobopslagDatasource } from '@features/letpensiondk/letpensiondk-jobopslag/jobopslag.datasource';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { JobopslagService } from '@features/letpensiondk/letpensiondk-jobopslag/jobopslag.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-letpensiondk-jobopslag-detail',
  imports: [
    ReactiveFormsModule,
    InputText,
    ButtonModule,
    MessageModule,
    ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './letpensiondk-jobopslag-detail.component.html',
  styleUrl: './letpensiondk-jobopslag-detail.component.css'
})
export class LetpensiondkJobopslagDetailComponent {
  confirmationService = inject(ConfirmationService);
  jobopslagService = inject(JobopslagService);
  jobopslagApi = inject(JobopslagDatasource);
  form: FormGroup;

  saving = signal(false);

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      beskrivelse: ['', { validators: [Validators.required] }],
      jobLink: [''],
      raekkeFoelge: [''],
      oprettet: new FormControl<Date | undefined>(undefined, {
        validators: [Validators.required],
      }),
    });

    effect(() => {
      const jobopslag = this.jobopslagService.selectedJobopslag();
      if (!jobopslag) {
        this.form.reset();
        this.form.patchValue({
          oprettet: dayjs().toDate()
        });
      } else {
        this.form.setValue({
          beskrivelse: jobopslag.beskrivelse || '',
          jobLink: jobopslag.jobLink || '',
          raekkeFoelge: jobopslag.raekkeFoelge || '',
          oprettet: jobopslag.oprettet || '',
        });
      }
    });
  }

  async onClickSave() {
    await this.saveChanges();
  }

  async onClickDelete(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Er du sikker?',
      header: 'Slet opslag',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger',
      },

      accept: async () => {
        const id=this.jobopslagService.selectedJobopslag()?.id;
        if (id){
          await this.jobopslagApi.remove(id);
        }
        this.jobopslagService.activeTab.set('list');
      },
    });

  }

  async saveChanges() {
    if (this.form.invalid) {
      return;
    }

    this.saving.set(true);

    const jobopslag = this.jobopslagService.selectedJobopslag();

    const newData = {
      ...jobopslag,
      ...this.form.value
    } as Jobopslag;

    if (jobopslag?.id) {
      await this.jobopslagApi.update(jobopslag.id, newData);
    } else {
      await this.jobopslagApi.create(newData);
    }

    this.saving.set(false);
    this.jobopslagService.activeTab.set('list');
  }
}
